// kast-dev family file: ErrorBoundary; master: waypoint_wars/ui/web/src/components/ErrorBoundary.tsx
// Copied unchanged into every kast-dev web app. Change the master, copy it
// everywhere, and run infra/scripts/check-family-files.sh.
//
// Render it inside <main>, below the header, keyed on the route path:
//   <ErrorBoundary key={location.pathname} ...><Outlet /></ErrorBoundary>
// so the header stays usable after a crash and navigating away clears it.

import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  /** Where the crash is recorded: the app's logger. Defaults to console.error. */
  onError?: (error: Error, info: ErrorInfo) => void;
  /** Opens the app's bug report pre-filled with the crash. Omitted: no Report button. */
  onReport?: (description: string) => void;
  /** The app's own button classes, so the screen looks like the rest of the site. */
  buttonClassName?: string;
  primaryButtonClassName?: string;
}

interface State {
  error: Error | null;
}

/**
 * Catches render-phase crashes so the page shows something recoverable
 * instead of going blank.
 *
 * Deliberately plain: no hooks, no context, no app styles beyond the classes
 * passed in, because the crash may have come from inside a provider and
 * nothing contextual is safe to depend on here.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (this.props.onError) this.props.onError(error, info);
    else console.error(`[Crash] ${error.message}`, info.componentStack ?? "");
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const { onReport, buttonClassName, primaryButtonClassName } = this.props;

    return (
      <div role="alert" style={{ padding: 24, maxWidth: 800, margin: "0 auto" }}>
        <h2 style={{ marginBottom: 12 }}>Something went wrong</h2>
        <p style={{ marginBottom: 12 }}>{error.message}</p>
        <p style={{ opacity: 0.7, fontSize: 13, marginBottom: 16 }}>
          {onReport
            ? "The error was recorded. Reporting it sends the logs from just before the crash."
            : "Try again, or reload the page."}
        </p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button type="button" className={primaryButtonClassName ?? buttonClassName} onClick={() => this.setState({ error: null })}>
            Try Again
          </button>
          {onReport && (
            <button
              type="button"
              className={buttonClassName}
              onClick={() => {
                // Cleared first: the report form must render in place of the
                // crashed tree, not inside it.
                const description = `Crash: ${error.message}`;
                this.setState({ error: null });
                onReport(description);
              }}
            >
              Report This
            </button>
          )}
          <button type="button" className={buttonClassName} onClick={() => window.location.reload()}>
            Reload
          </button>
        </div>
        {error.stack && (
          <pre style={{ marginTop: 16, fontSize: 11, opacity: 0.6, overflowX: "auto", whiteSpace: "pre-wrap" }}>
            {error.stack}
          </pre>
        )}
      </div>
    );
  }
}
